import React, { useEffect, useMemo, useState } from 'react';
import {
	Box,
	Button,
	IconButton,
	Typography,
	useMediaQuery,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { PropertyDefinition, Theme } from '../types';
import { useAppSelector } from '../hooks';
import {
	selectAllEntryProperties,
	selectAllPropertyDefinitions,
	selectAllRepositories,
	selectAllRepositoryEntries,
} from '../containers/contentSlice';
import { selectIsAuthenticated } from '../containers/authSlice';
import { RepositoryEntriesRowDto, useGetRepositoryByIdQuery, useGetRepositoryEntriesByFilterQuery } from '../services/repositoryEntries';
import { populateRepositoryEntriesForTable, sortEntryByRank } from '../common/util';
import { CanvasCardContent } from './CanvasCardFrame';
import { REFERENCE_CARD_SX, RepositoryEntryCardBody } from './CanvasReferenceCard';
import useAuth from './Auth';

export interface RepositoryEntryPickerProps {
	/** Loads entries and resets the selection whenever the picker becomes active. */
	active: boolean;
	repositoryId: string | null;
	/** Which non-archived entries are offered for picking. */
	isEntryPickable: (row: RepositoryEntriesRowDto) => boolean;
	/** Subtitle under the picker title. */
	hint: string;
	/** Text of the confirm button for `count` selected entries. */
	confirmLabel: (count: number) => string;
	onClose: () => void;
	onConfirm: (selectedRepositoryEntryIds: string[]) => void;
	onCreateNewClick: () => void;
}

/**
 * Full-screen grid of entry cards for picking existing repository entries.
 */
const RepositoryEntryPicker: React.FC<RepositoryEntryPickerProps> = ({
	active,
	repositoryId,
	isEntryPickable,
	hint,
	confirmLabel,
	onClose,
	onConfirm,
	onCreateNewClick,
}) => {
	const isAuthenticated = useAppSelector(selectIsAuthenticated);
	const allRepositories = useAppSelector(selectAllRepositories);
	const allRepositoryEntries = useAppSelector(selectAllRepositoryEntries);
	const allPropertyDefinitions = useAppSelector(selectAllPropertyDefinitions);
	const allEntryProperties = useAppSelector(selectAllEntryProperties);
	const { isAuthenticating } = useAuth();
	const isMdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));

	const skipRemote = !active || !repositoryId || !isAuthenticated || isAuthenticating;

	const { data: repositoryById } = useGetRepositoryByIdQuery(repositoryId ?? '', { skip: skipRemote });
	const { data: repositoryActiveEntriesResponse } = useGetRepositoryEntriesByFilterQuery({
		repositoryId: repositoryId ?? '',
		includeArchived: false,
	}, { skip: skipRemote });

	const repository = useMemo(() => {
		if (isAuthenticated) return repositoryById?.repository;
		return allRepositories.find((r) => r.id === repositoryId);
	}, [allRepositories, isAuthenticated, repositoryById?.repository, repositoryId]);

	const propertyDefinitions = useMemo(() => {
		const defs = isAuthenticated
			? (repositoryById?.propertyDefinitions ?? [])
			: allPropertyDefinitions.filter((def) => def.parentId === repositoryId);
		return [...defs].sort(sortEntryByRank) as PropertyDefinition[];
	}, [allPropertyDefinitions, isAuthenticated, repositoryById?.propertyDefinitions, repositoryId]);

	const repositoryColor = repository?.color ?? undefined;

	const rows = useMemo(() => {
		if (!repositoryId) return [];
		if (isAuthenticated) {
			return (repositoryActiveEntriesResponse?.rows ?? []).filter(isEntryPickable);
		}
		if (!repository) return [];
		return populateRepositoryEntriesForTable(repository, allRepositoryEntries, allPropertyDefinitions, allEntryProperties)
			.filter((row) => !row.isArchived && isEntryPickable(row));
	}, [
		allEntryProperties,
		allPropertyDefinitions,
		allRepositoryEntries,
		isAuthenticated,
		isEntryPickable,
		repository,
		repositoryActiveEntriesResponse?.rows,
		repositoryId,
	]);

	const [selectedIds, setSelectedIds] = useState<string[]>([]);

	useEffect(() => {
		if (active) {
			setSelectedIds([]);
		}
	}, [active, repositoryId]);

	const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

	const toggle = (id: string) => {
		setSelectedIds((prev) =>
			prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
		);
	};

	return (
		<Box display="flex" flexDirection="column" height="100%" position="relative">
			<Box sx={{ flex: 1, overflowY: 'auto' }}>
				<Box 
					px={3}
					py={1.5}
				>
					<Box
						display="flex"
						alignItems="center"
						gap={2}
						sx={{
							position: isMdUp ? 'sticky' : 'static',
							top: 0,
							zIndex: 1,
							userSelect: 'none',
							flexWrap: 'wrap',
						}}
					>
						<Box flex={1} minWidth={0}>
							<Typography variant="h6">Pick from {repository?.title ?? ''}</Typography>
							<Typography variant="body2" color="text.secondary">{hint}</Typography>
						</Box>
						{isMdUp && <Button
							variant="outlined"
							color="primary"
							startIcon={<AddIcon fontSize="small" />}
							onClick={onCreateNewClick}
						>
							Create new
						</Button>}
						<IconButton aria-label="Close" onClick={onClose}>
							<CloseIcon />
						</IconButton>
					</Box>
					{!isMdUp && <Box display='flex' mt={2}>
						<Button
							variant="outlined"
							color="primary"
							startIcon={<AddIcon fontSize="small" />}
							onClick={onCreateNewClick}
						>
							Create new
						</Button>
					</Box>}
				</Box>
				<Box
					display="flex"
					flexWrap="wrap"
					alignItems="flex-start"
					alignContent="flex-start"
					gap={2}
					p={3}
					pb={12}
					sx={{ userSelect: 'none' }}
				>
					{rows.map((row) => {
						const isSelected = selectedSet.has(row.id);
						return (
							<CanvasCardContent
								key={row.id}
								role="checkbox"
								aria-checked={isSelected}
								tabIndex={0}
								collectionColor={repositoryColor}
								isHighlighted={isSelected}
								wideMinWidth
								onClick={() => toggle(row.id)}
								onKeyDown={(e: React.KeyboardEvent) => {
									if (e.key === ' ' || e.key === 'Enter') {
										e.preventDefault();
										toggle(row.id);
									}
								}}
								sx={[
									REFERENCE_CARD_SX,
									{
										width: 260,
										cursor: 'pointer',
										userSelect: 'none',
									},
								]}
							>
								<RepositoryEntryCardBody
									repositorySingular={repository?.singular ?? undefined}
									collectionColor={repositoryColor}
									propertyDefinitions={propertyDefinitions}
									row={row}
								/>
							</CanvasCardContent>
						);
					})}
					{!rows.length && (
						<Typography variant="body1" color="text.secondary" sx={{ fontStyle: 'italic' }}>
							No entries yet
						</Typography>
					)}
				</Box>
			</Box>
			{selectedIds.length > 0 && (
				<Box sx={{ position: 'absolute', bottom: 32, left: '50%', transform: 'translateX(-50%)' }}>
					{/* Keyed by count so every selection change remounts the button and replays the bounce. */}
					<Button
						key={selectedIds.length}
						variant="contained"
						size="large"
						onClick={() => onConfirm(selectedIds)}
						sx={{
							animation: 'addButtonBounce 400ms ease-out',
							'@keyframes addButtonBounce': {
								'0%': { transform: 'scale(1)' },
								'35%': { transform: 'scale(1.15)' },
								'65%': { transform: 'scale(0.95)' },
								'100%': { transform: 'scale(1)' },
							},
						}}
					>
						{confirmLabel(selectedIds.length)}
					</Button>
				</Box>
			)}
		</Box>
	);
};

export default RepositoryEntryPicker;
